import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiPackage, FiActivity, FiDollarSign, FiUser } from 'react-icons/fi';
import { accountAPI, productAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import useResponsive from '../hooks/useResponsive';

const Dashboard = () => {
  const [accountInfo, setAccountInfo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useAuth();
  const { isMobile, isTablet, isDesktop } = useResponsive();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [accountRes, productsRes] = await Promise.all([
        accountAPI.getMe(),
        productAPI.list()
      ]);
      
      if (accountRes.data.success) {
        setAccountInfo(accountRes.data.data);
        setUser(accountRes.data.data);
      }
      
      if (productsRes.data.success) {
        setProducts(productsRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBalanceDisplay = () => {
    if (!accountInfo) return '0';
    
    const role = accountInfo.role;
    const balances = accountInfo.balances || {};
    
    if (role === 'system' || role === 'super_admin') {
      // Show PH and BR balances
      const phBalance = balances.PH || 0;
      const brBalance = balances.BR || 0;
      return `PH: ${phBalance} | BR: ${brBalance}`;
    } else {
      // Show MMK balance for user and reseller
      return `${balances.MMK || 0} MMK`;
    }
  };

  const getProductDetails = (product) => {
    if (product.is_stock_based === 1) {
      return `Code: ${product.code} | Stock Based`;
    } else {
      return `Code: ${product.code} | Currency: ${product.use_currency || 'N/A'} | API: ${product.api_code || 'N/A'}`;
    }
  };

  const statsCards = [
    {
      title: 'Total Products',
      value: products?.length || 0,
      icon: FiPackage,
      bgColor: 'bg-blue-500',
      lightBg: 'bg-blue-50',
      textColor: 'text-blue-600',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Active Products',
      value: products?.filter(p => p.is_active === 1)?.length || 0,
      icon: FiActivity,
      bgColor: 'bg-green-500',
      lightBg: 'bg-green-50',
      textColor: 'text-green-600',
      iconColor: 'text-green-600'
    },
    {
      title: 'Balance',
      value: getBalanceDisplay(),
      icon: FiDollarSign,
      bgColor: 'bg-purple-500',
      lightBg: 'bg-purple-50',
      textColor: 'text-purple-600',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Account',
      value: accountInfo?.account_name || 'N/A',
      icon: FiUser,
      bgColor: 'bg-orange-500',
      lightBg: 'bg-orange-50',
      textColor: 'text-orange-600',
      iconColor: 'text-orange-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-6 text-white"
      >
        <h2 className="text-2xl font-bold mb-2">
          Welcome back, {accountInfo?.account_name || 'Admin'}!
        </h2>
        <p className="text-blue-100">
          Here's what's happening with your dashboard today.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className={`grid ${isMobile ? 'grid-cols-2' : 'grid-cols-4'} gap-4`}>
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {stat.title}
                  </p>
                  <p className="text-xl font-bold text-gray-900 dark:text-white">
                    {stat.value}
                  </p>
                </div>
                <div className={`p-3 ${stat.lightBg} dark:bg-gray-700 rounded-lg`}>
                  <Icon className={`w-5 h-5 ${stat.iconColor} dark:text-gray-300`} />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Recent Products */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Recent Products
        </h3>
        
        <div className="space-y-3">
          {products.slice(0, 5).map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <div className="flex-1">
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {product.name}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {getProductDetails(product)}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  product.is_active === 1
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {product.is_active === 1 ? 'Active' : 'Inactive'}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Quick Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Account Overview
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Email</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {accountInfo?.email}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Role</span>
              <span className="text-gray-900 dark:text-white font-medium capitalize">
                {accountInfo?.role}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Balance</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {getBalanceDisplay()}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600 dark:text-gray-400">Member Since</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {new Date(accountInfo?.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Stats
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Total Products</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {products?.length || 0}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Active Products</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {products?.filter(p => p.is_active === 1)?.length || 0}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Stock Based</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {products?.filter(p => p.is_stock_based === 1)?.length || 0}
              </span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600 dark:text-gray-400">Non-Stock</span>
              <span className="text-gray-900 dark:text-white font-medium">
                {products?.filter(p => p.is_stock_based === 0)?.length || 0}
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;